import React, { useState, useEffect } from "react";
import { Accordion, Table, Button, Spinner, Alert } from "react-bootstrap";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  REMCHANNEL_COLORS,
  LEGEND_LABEL_OVERRIDES,
  formatLegendLabel,
  getHiddenSections,
  CHART_PALETTE,
} from "./report/reportTheme";
import StiResultsSection from "./report/StiResultsSection";
import LtiResultsSection from "./report/LtiResultsSection";
import { getUserRoles, exportProcessedDataToExcel, exportPercentileSourceDataToExcel, downloadPercentileSourceDataCsvForGrade, retranslateReportGrades, recalculatePercentiles, triggerDataSync } from "../services/api";

const ReportSections = ({ reportData, reportId, circleId, circleName, gradingSystemId, gradingSystemName, organizationId, onReportRefresh }) => {
  const [hasAllowedRole, setHasAllowedRole] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPercentileData, setExportingPercentileData] = useState(false);
  const [exportingGradeCsv, setExportingGradeCsv] = useState(null);
  const [retranslating, setRetranslating] = useState(false);
  const [recalculatingPercentiles, setRecalculatingPercentiles] = useState(false);
  const [syncingData, setSyncingData] = useState(false);
  const orgId = localStorage.getItem("orgId");
  const isEmbedded = window.self !== window.top;
  
  // Get hidden sections configuration
  const hiddenSections = getHiddenSections();

  useEffect(() => {
    const checkUserRoles = async () => {
      if (orgId) {
        try {
          const roles = await getUserRoles();
          // Only apply role restrictions in embedded mode
          if (isEmbedded) {
            const allowedRoles = [1, 2, 3, 4, 5];
            const hasRole = roles.some((role) =>
              allowedRoles.includes(role.RoleID)
            );
            setHasAllowedRole(hasRole);
          } else {
            // In non-embedded mode, always allow access
            setHasAllowedRole(true);
          }
        } catch (error) {
          console.error("Error checking user roles:", error);
        }
      }
    };

    checkUserRoles();
  }, [orgId, isEmbedded]);

  if (!reportData) {
    return (
      <Accordion
        className="mt-4"
        id="report-sections-content"
        defaultActiveKey={["0"]}
      >
        <Accordion.Item eventKey="0">
          <Accordion.Header>NO DATA AVAILABLE</Accordion.Header>
          <Accordion.Body>
            <p>
              Please wait for the report data to load or try regenerating the
              report.
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  }

  // Color arrays for charts
  const ltiEligibilityColors = CHART_PALETTE;
  const ltiPlansColors = CHART_PALETTE;
  const ltiPerformanceConditionsColors = CHART_PALETTE;
  const ltiVestingMethodologyColors = CHART_PALETTE;
  const ltiVestingPeriodsColors = CHART_PALETTE;
  const ltiSettlementMethodologyColors = CHART_PALETTE;
  const stiEligibilityColors = CHART_PALETTE;
  const stiPerformanceAssessmentColors = CHART_PALETTE;
  const stiPlanTypesPerformanceConditionsColors = CHART_PALETTE;
  return (
    <Accordion
      className="mt-4"
      id="report-sections-content"
      defaultActiveKey={["0", "1", "2", "3"]}
      alwaysOpen
    >
      <Accordion.Item eventKey="0">
        <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
          INTRODUCTION AND METHODOLOGY
        </Accordion.Header>
        <Accordion.Body>
          <p>
            This report provides an analysis of total remuneration practices among the organisations that participated in the survey. It presents information on both long-term and short-term incentives, as well as trends in overall remuneration mix across various employee levels, to support pay structuring decisions.
          </p>
          <p>
            The survey included both qualitative and quantitative questions, which were answered based on each company’s grading system. The findings address practices related to:
          </p>
          <ul>
            <li>Long-term incentives (LTI)</li>
            <li>Short-term incentives (STI)</li>
            <li>Total Remuneration Mix</li>
          </ul>
          <p>
            A total of {reportData.metadata.totalQuestionnaires || "—"} organisations participated in the Total Remuneration survey, and this report is based on their REMchannel TGP data submissions as well as their LTI and STI survey responses.
          </p>
          <p>
            A validation process, using the REMchannel methodology, was applied to data on total guaranteed package (TGP). However, information concerning short- and long-term incentives is reported as submitted by participants.
          </p>
          <h6 className="mt-4 mb-2"><b>Caveats and limitations</b></h6>
          <ul>
            <li>The analysis and opinions in this document are based on:</li>
            <ul>
              <li>The facts as presented to the consultants via the data collection process. It must be noted that REMchannel cannot take any responsibility for inaccuracies and the scheme cannot be held responsible for incorrect results as a result of such inaccuracies or data.</li>
              <li>Our knowledge and interpretation of the data as portrayed in discussion with representatives of the participating organisations.</li>
            </ul>
          </ul>

          {hasAllowedRole && (
            <div className="d-flex justify-content-end gap-2 mb-3">
              <Button
                variant="outline-primary"
                onClick={async () => {
                  try {
                    setRetranslating(true);
                    const reportIdToUse = reportId || reportData?.metadata?.reportId || 'unknown';
                    await retranslateReportGrades(reportIdToUse);
                    
                    // Refresh the report data if callback is provided
                    if (onReportRefresh) {
                      await onReportRefresh();
                    } else {
                      // Fallback: show success message and suggest page refresh
                      alert('Report grades retranslated successfully. Please refresh the page to see the updated data.');
                    }
                  } catch (error) {
                    console.error('Error retranslating report grades:', error);
                    alert(`Failed to retranslate report grades: ${error.message}`);
                  } finally {
                    setRetranslating(false);
                  }
                }}
                disabled={retranslating || !reportId}
              >
                {retranslating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Retranslating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Retranslate Grades
                  </>
                )}
              </Button>
              
              <Button
                variant="outline-secondary"
                onClick={async () => {
                  try {
                    setExportingExcel(true);
                    const metadata = reportData.metadata;
                    const reportIdToUse = reportId || metadata?.reportId || 'unknown';
                    const circleIdToUse = circleId || metadata?.circleId || reportData?.circleId;
                    const gradingSystemIdToUse = gradingSystemId || metadata?.gradingSystemId || reportData?.gradingSystemId;
                    const orgIdToUse = organizationId || orgId || metadata?.organizationId;
                    
                    await exportProcessedDataToExcel(
                      reportIdToUse,
                      metadata,
                      circleIdToUse,
                      gradingSystemIdToUse,
                      orgIdToUse,
                      hasAllowedRole, // isKAMUser
                      circleName || circleIdToUse, // circleName - use prop if available, otherwise fallback to ID
                      gradingSystemName || gradingSystemIdToUse // gradingSystemName - will need to be passed from parent, but for now use ID as fallback
                    );
                  } catch (error) {
                    console.error('Error exporting processed data to Excel:', error);
                    alert(`Failed to export processed data: ${error.message}`);
                  } finally {
                    setExportingExcel(false);
                  }
                }}
                disabled={exportingExcel || !reportData?.metadata}
              >
                {exportingExcel ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-excel me-2"></i>
                    Export to Excel
                  </>
                )}
              </Button>
            </div>
          )}
        </Accordion.Body>
      </Accordion.Item>

      <LtiResultsSection reportData={reportData} />

      <StiResultsSection reportData={reportData} />

      {!hiddenSections.totalRemunerationPayMixOldVersion && (
      <Accordion.Item eventKey="3">
        <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
          TOTAL REMUNERATION PAY MIX
        </Accordion.Header>
        <Accordion.Body>
          <h5>Total remuneration pay mix</h5>

          {(() => {
            // Defensive: check data exists
            const stiEligibility = reportData.chartDataByGradingSystem.stiEligibility;
            const stiTarget = reportData.chartDataByGradingSystem.stiTargetAnnualReward;
            const ltiEligibility = reportData.chartDataByGradingSystem.ltiEligibility;
            const ltiTarget = reportData.chartDataByGradingSystem.ltiTargetAnnualReward;

            // Helper to get grades with eligibility (None != 100)
            function getEligibleGrades(eligibility) {
              if (
                eligibility &&
                Array.isArray(eligibility.labels) &&
                Array.isArray(eligibility.datasets)
              ) {
                const noneIdx = eligibility.datasets.findIndex(
                  ds => ds.label && ds.label.toLowerCase().includes("none")
                );
                if (noneIdx !== -1) {
                  return eligibility.labels.filter((grade, i) => {
                    const val = eligibility.datasets[noneIdx].data[i];
                    return Number(val) !== 100;
                  });
                }
              }
              return [];
            }

            // Helper to get grades present in a dataset
            function getGradesPresent(target) {
              return new Set(target?.labels || []);
            }

            // STI
            const stiEligibleGrades = getEligibleGrades(stiEligibility);
            const stiPresentGrades = getGradesPresent(stiTarget);
            const stiMissingGrades = stiEligibleGrades.filter(
              grade => !stiPresentGrades.has(grade)
            );

            // LTI
            const ltiEligibleGrades = getEligibleGrades(ltiEligibility);
            const ltiPresentGrades = getGradesPresent(ltiTarget);
            const ltiMissingGrades = ltiEligibleGrades.filter(
              grade => !ltiPresentGrades.has(grade)
            );

            if (stiMissingGrades.length > 0 || ltiMissingGrades.length > 0) {
              return (
                <div className="alert alert-warning my-2" style={{ fontSize: "0.95em" }}>
                  <strong>Note:</strong>{" "}
                  {stiMissingGrades.length > 0 && (
                    <>
                      The following grades have STI eligibility but are not shown in the STI tables below due to insufficient STI data:{" "}
                      <span style={{ fontWeight: 700 }}>
                        {stiMissingGrades.join(", ")}
                      </span>
                      <br />
                    </>
                  )}
                  {ltiMissingGrades.length > 0 && (
                    <>
                      The following grades have LTI eligibility but are not shown in the LTI tables below due to insufficient LTI data:{" "}
                      <span style={{ fontWeight: 700 }}>
                        {ltiMissingGrades.join(", ")}
                      </span>
                    </>
                  )}
                </div>
              );
            }
            return null;
          })()}

          
          <p>
          The table below illustrates the market median (50th percentile) based percentages of Total Guaranteed Package (TGP), Short-Term Incentives (STI), and Long-Term Incentives (LTI), which together constitute the total remuneration mix for each grade.
          </p>
          {reportData.chartDataByGradingSystem?.stiTargetAnnualReward &&
          reportData.chartDataByGradingSystem?.ltiTargetAnnualReward ? (
            <div style={{ height: "500px", margin: "2rem 0", width: "100%" }}>
              <Bar
                data={(() => {
                  // Defensive: check data exists
                  const stiData =
                    reportData.chartDataByGradingSystem.stiTargetAnnualReward;
                  const ltiData =
                    reportData.chartDataByGradingSystem.ltiTargetAnnualReward;
                  if (
                    !stiData ||
                    !stiData.labels ||
                    !stiData.datasets ||
                    stiData.labels.length === 0 ||
                    !ltiData ||
                    !ltiData.labels ||
                    !ltiData.datasets ||
                    ltiData.labels.length === 0
                  ) {
                    return { labels: [], datasets: [] };
                  }

                  // Helper to find dataset index by label substring
                  function findDatasetIndex(datasets, labelSubstring) {
                    return datasets.findIndex(ds =>
                      ds.label && ds.label.toLowerCase().includes(labelSubstring.toLowerCase())
                    );
                  }

                  // Find TGP Average and Base Salary P50 for both STI and LTI
                  const stiTgpAvgIdx = findDatasetIndex(stiData.datasets, "TGP Average");
                  const stiBaseP50Idx = findDatasetIndex(stiData.datasets, "Base Salary P50");
                  const ltiTgpAvgIdx = findDatasetIndex(ltiData.datasets, "TGP Average");
                  const ltiBaseP50Idx = findDatasetIndex(ltiData.datasets, "Base Salary P50");

                  // Use all unique grades from both STI and LTI
                  const uniqueGradesSet = new Set([
                    ...stiData.labels,
                    ...ltiData.labels,
                  ]);
                  // Sort by grade name (alphanumeric)
                  const uniqueGrades = Array.from(uniqueGradesSet).sort((a, b) =>
                    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
                  );

                  const tgpData = [];
                  const stiDataArr = [];
                  const ltiDataArr = [];

                  uniqueGrades.forEach((grade) => {
                    // STI values
                    let stiTgp = null, stiValue = null;
                    const stiIdx = stiData.labels.indexOf(grade);
                    if (stiIdx !== -1 && stiTgpAvgIdx !== -1 && stiBaseP50Idx !== -1) {
                      stiTgp = Number(stiData.datasets[stiTgpAvgIdx].data[stiIdx]);
                      stiValue = Number(stiData.datasets[stiBaseP50Idx].data[stiIdx]);
                      if (isNaN(stiTgp)) stiTgp = null;
                      if (isNaN(stiValue)) stiValue = null;
                    }

                    // LTI values
                    let ltiTgp = null, ltiValue = null;
                    const ltiIdx = ltiData.labels.indexOf(grade);
                    if (ltiIdx !== -1 && ltiTgpAvgIdx !== -1 && ltiBaseP50Idx !== -1) {
                      ltiTgp = Number(ltiData.datasets[ltiTgpAvgIdx].data[ltiIdx]);
                      ltiValue = Number(ltiData.datasets[ltiBaseP50Idx].data[ltiIdx]);
                      if (isNaN(ltiTgp)) ltiTgp = null;
                      if (isNaN(ltiValue)) ltiValue = null;
                    }

                    // Use TGP from STI if available, else from LTI, else 0
                    let tgp = stiTgp !== null ? stiTgp : (ltiTgp !== null ? ltiTgp : 0);

                    // Calculate STI % of TGP
                    let stiPct = null;
                    if (stiTgp !== null && stiValue !== null && stiTgp > 0) {
                      stiPct = (stiValue / stiTgp) * 100;
                    }

                    // Calculate LTI % of TGP
                    let ltiPct = null;
                    if (ltiTgp !== null && ltiValue !== null) {
                      // Use TGP from STI if available and > 0, else from LTI
                      let tgpForLti = stiTgp !== null && stiTgp > 0 ? stiTgp : ltiTgp;
                      if (tgpForLti > 0) {
                        ltiPct = (ltiValue / tgpForLti) * 100;
                      }
                    }

                    // TGP is always 100% of itself (raw)
                    const tgpPctRaw = 100;

                    // If both STI and LTI are missing, just show TGP as 100%
                    let tgpScaled = 100, stiScaled = 0, ltiScaled = 0;
                    if (
                      (typeof stiPct === "number" && !isNaN(stiPct) && stiPct > 0) ||
                      (typeof ltiPct === "number" && !isNaN(ltiPct) && ltiPct > 0)
                    ) {
                      // If either STI or LTI is present, scale all three so their sum is 100%
                      const totalRaw = tgpPctRaw + (stiPct || 0) + (ltiPct || 0);
                      if (totalRaw > 0) {
                        tgpScaled = (tgpPctRaw / totalRaw) * 100;
                        stiScaled = ((stiPct || 0) / totalRaw) * 100;
                        ltiScaled = ((ltiPct || 0) / totalRaw) * 100;
                      }
                    }

                    tgpData.push(Math.round(tgpScaled));
                    stiDataArr.push(Math.round(stiScaled));
                    ltiDataArr.push(Math.round(ltiScaled));
                  });

                  return {
                    labels: uniqueGrades,
                    datasets: [
                      {
                        label: "TGP",
                        data: tgpData,
                        backgroundColor: REMCHANNEL_COLORS.steelBlue,
                        stack: "Stack 0",
                      },
                      {
                        label: "On-target STI",
                        data: stiDataArr,
                        backgroundColor: REMCHANNEL_COLORS.green,
                        stack: "Stack 0",
                      },
                      {
                        label: "On-target LTI",
                        data: ltiDataArr,
                        backgroundColor: REMCHANNEL_COLORS.orange,
                        stack: "Stack 0",
                      },
                    ],
                  };
                })()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      stacked: true,
                      title: {
                        display: true,
                        text: "Grade",
                      },
                    },
                    y: {
                      stacked: true,
                      title: {
                        display: true,
                        text: "Percentage of Total Remuneration",
                      },
                      ticks: {
                        callback: function (value) {
                          return value + "%";
                        },
                      },
                    },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return (
                            formatLegendLabel(context.dataset.label) + ": " + context.raw + "%"
                          );
                        },
                      },
                    },
                    legend: {
                      position: "bottom",
                      labels: {
                        boxWidth: 12,
                        generateLabels: (chart) => {
                          const datasets = chart.data.datasets;
                          return datasets.map((dataset, i) => ({
                            text: formatLegendLabel(dataset.label || ""),
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.borderColor,
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                          }));
                        },
                      },
                    },
                    datalabels: {
                      formatter: (value) => {
                        return value > 0 ? value + "%" : "";
                      },
                      color: "#fff",
                      font: {
                        weight: "bold",
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="text-center my-4">No pay mix data available</div>
          )}

          <h5>Total Remuneration pay mix for each grade. </h5>
         
          <Table striped bordered hover responsive className="mt-3">
            <thead>
              <tr>
                <th style={{ width: "120px" }}>Grade</th>
                <th style={{ width: "120px" }}>TGP %</th>
                <th style={{ width: "120px" }}>On-target STI</th>
                <th style={{ width: "120px" }}>On-target LTI</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const stiData =
                  reportData.chartDataByGradingSystem?.stiTargetAnnualReward;
                const ltiData =
                  reportData.chartDataByGradingSystem?.ltiTargetAnnualReward;

                if (
                  !stiData?.labels ||
                  !stiData?.datasets ||
                  stiData.labels.length === 0 ||
                  !ltiData?.labels ||
                  !ltiData?.datasets ||
                  ltiData.labels.length === 0
                ) {
                  return (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No data available
                      </td>
                    </tr>
                  );
                }

                // Helper to find dataset index by label substring
                function findDatasetIndex(datasets, labelSubstring) {
                  return datasets.findIndex(ds =>
                    ds.label && ds.label.toLowerCase().includes(labelSubstring.toLowerCase())
                  );
                }

                // Find TGP Average and Base Salary P50 for both STI and LTI
                const stiTgpAvgIdx = findDatasetIndex(stiData.datasets, "TGP Average");
                const stiBaseP50Idx = findDatasetIndex(stiData.datasets, "Base Salary P50");
                const ltiTgpAvgIdx = findDatasetIndex(ltiData.datasets, "TGP Average");
                const ltiBaseP50Idx = findDatasetIndex(ltiData.datasets, "Base Salary P50");

                // Use all unique grades from both STI and LTI, sorted by grade name
                const uniqueGradesSet = new Set([
                  ...stiData.labels,
                  ...ltiData.labels,
                ]);
                const uniqueGrades = Array.from(uniqueGradesSet).sort((a, b) =>
                  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
                );

                return uniqueGrades.map((grade) => {
                  // STI values
                  let stiTgp = null, stiValue = null;
                  const stiIdx = stiData.labels.indexOf(grade);
                  if (stiIdx !== -1 && stiTgpAvgIdx !== -1 && stiBaseP50Idx !== -1) {
                    stiTgp = Number(stiData.datasets[stiTgpAvgIdx].data[stiIdx]) || 0;
                    stiValue = Number(stiData.datasets[stiBaseP50Idx].data[stiIdx]) || 0;
                  }

                  // LTI values
                  let ltiTgp = null, ltiValue = null;
                  const ltiIdx = ltiData.labels.indexOf(grade);
                  if (ltiIdx !== -1 && ltiTgpAvgIdx !== -1 && ltiBaseP50Idx !== -1) {
                    ltiTgp = Number(ltiData.datasets[ltiTgpAvgIdx].data[ltiIdx]) || 0;
                    ltiValue = Number(ltiData.datasets[ltiBaseP50Idx].data[ltiIdx]) || 0;
                  }

                  // Use TGP from STI if available, else from LTI, else 0
                  let tgp = stiTgp !== null ? stiTgp : (ltiTgp !== null ? ltiTgp : 0);

                  // Calculate STI % of TGP
                  let stiPct = 0;
                  if (stiTgp && stiValue) {
                    stiPct = (stiValue / stiTgp) * 100;
                  }

                  // Calculate LTI % of TGP
                  let ltiPct = 0;
                  if (ltiTgp && ltiValue) {
                    // Use TGP from LTI if STI TGP not available
                    let tgpForLti = stiTgp !== null ? stiTgp : ltiTgp;
                    if (tgpForLti) {
                      ltiPct = (ltiValue / tgpForLti) * 100;
                    }
                  }

                  // If both STI and LTI are missing, just show TGP as 100%
                  let tgpScaled = 100, stiScaled = 0, ltiScaled = 0;
                  if (
                    (typeof stiPct === "number" && !isNaN(stiPct) && stiPct > 0) ||
                    (typeof ltiPct === "number" && !isNaN(ltiPct) && ltiPct > 0)
                  ) {
                    // If either STI or LTI is present, scale all three so their sum is 100%
                    const tgpPctRaw = 100;
                    const totalRaw = tgpPctRaw + (stiPct || 0) + (ltiPct || 0);
                    if (totalRaw > 0) {
                      tgpScaled = (tgpPctRaw / totalRaw) * 100;
                      stiScaled = ((stiPct || 0) / totalRaw) * 100;
                      ltiScaled = ((ltiPct || 0) / totalRaw) * 100;
                    }
                  }

                  // Round for display
                  tgpScaled = Math.round(tgpScaled);
                  stiScaled = Math.round(stiScaled);
                  ltiScaled = Math.round(ltiScaled);

                  return (
                    <tr key={`pay-mix-${grade}`}>
                      <td>{grade}</td>
                      <td>{tgpScaled}%</td>
                      <td>{stiScaled}%</td>
                      <td>{ltiScaled}%</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>
      )}

      {reportData.dataAugmentedPayMix && (
        <Accordion.Item eventKey="4">
          <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
            LTI Data Collection Pay Mix
          </Accordion.Header>
          <Accordion.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5>LTI Data Collection Pay Mix Percentiles by {circleName || 'Circle'}</h5>
                <p className="text-muted mb-0">
                  Percentiles calculated from TGP, LTI Amount, On Target LTI Amount, STI Amount, and On Target STI Amount values in Remchannel for all organizations in the circle.
                  The requesting organization's data is excluded from calculations.
                </p>
              </div>
              {false && hasAllowedRole && (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={async () => {
                      try {
                        setSyncingData(true);
                        const orgIdToUse = organizationId || orgId;
                        if (!orgIdToUse) {
                          alert('Organization ID is required to sync data');
                          return;
                        }
                        const result = await triggerDataSync(orgIdToUse);
                        alert(`Data sync job queued successfully. Job ID: ${result.jobId}. The sync may take a few minutes. You can recalculate percentiles after the sync completes.`);
                      } catch (error) {
                        console.error('Error syncing data:', error);
                        alert(`Failed to sync data: ${error.response?.data?.message || error.message || 'Unknown error'}`);
                      } finally {
                        setSyncingData(false);
                      }
                    }}
                    disabled={syncingData || !organizationId}
                    title="Sync employee data from MSSQL to PostgreSQL"
                  >
                    {syncingData ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Sync Data
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={async () => {
                      try {
                        setRecalculatingPercentiles(true);
                        const reportIdToUse = reportId || reportData?.metadata?.reportId || 'unknown';
                        const result = await recalculatePercentiles(reportIdToUse);
                        
                        // Refresh the report data if callback is provided
                        if (onReportRefresh) {
                          await onReportRefresh();
                        } else {
                          // Fallback: show success message and suggest page refresh
                          const message = result.validation?.isValid 
                            ? `Percentiles recalculated successfully. ${result.percentileCount || 0} percentile groups calculated. Please refresh the page to see the updated data.`
                            : `Percentiles recalculated with warnings: ${result.validation?.reasons?.join('; ') || 'Unknown issues'}. Please refresh the page to see the updated data.`;
                          alert(message);
                        }
                      } catch (error) {
                        console.error('Error recalculating percentiles:', error);
                        alert(`Failed to recalculate percentiles: ${error.message}`);
                      } finally {
                        setRecalculatingPercentiles(false);
                      }
                    }}
                    disabled={recalculatingPercentiles || !reportId || !reportData?.dataAugmentedPayMix}
                  >
                    {recalculatingPercentiles ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Recalculating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Recalculate Percentiles
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Conditions for percentile data to appear - only show for KAM users */}
            {hasAllowedRole && (
              <Alert variant="info" className="mb-3">
                <strong>Conditions for Percentile Data to Appear:</strong>
                <ul className="mb-0 mt-2">
                  <li>The "Data Augmented Pay Mix" checkbox must be enabled when creating the report</li>
                  <li><strong>Employee data must be synced from MSSQL to PostgreSQL</strong> - Data from UploadedEmployees (MSSQL) and SnapshotEmployeeData (MSSQL) must be synced to the <code>uploaded_employees</code> table (PostgreSQL). This sync happens automatically daily at 7 AM. <strong>Note:</strong> If PostgreSQL data is not available, the system will automatically fall back to calculating percentiles directly from MSSQL tables, but this will not include user-editable LTI amounts.</li>
                  <li>At least one organization (other than the requesting organization) must exist in the circle</li>
                  <li>Synced data must have <strong>grade_name populated</strong> (this is critical - data without grade_name will be excluded). The grade_name comes from SnapshotEmployeeData during the sync process.</li>
                  <li>Data must include <strong>at least one</strong> of: TGP, LTI Amount, On Target LTI Amount, STI, or On Target STI values (you don't need all fields - TGP and STI alone are sufficient). STI values come from SnapshotEmployeeData (FB17, FB40) during sync.</li>
                  <li>grade_name values must match valid Paterson Classic grade names in the system</li>
                  <li>Sufficient data points per grade are required for meaningful percentile calculations (minimum 10 data points and 3 organizations per field for percentiles)</li>
                </ul>
                <p className="mb-0 mt-2"><strong>Note:</strong> Percentiles are calculated independently for each field. If you have TGP and STI data but no LTI data, TGP and STI percentiles will still be calculated.</p>
              </Alert>
            )}
            
            {!reportData.dataAugmentedPayMixPercentiles || (Array.isArray(reportData.dataAugmentedPayMixPercentiles) && reportData.dataAugmentedPayMixPercentiles.length === 0) ? (
              <Alert variant="warning" className="mt-3">
                <strong>No percentile data available.</strong>
                <p className="mb-0 mt-2">
                  Percentile calculations may not have completed or there may be insufficient data. 
                  Please check the report generation logs or try regenerating the report with the "Data Augmented Pay Mix" option enabled.
                </p>
                {reportData.dataAugmentationValidation && !reportData.dataAugmentationValidation.isValid && (
                  <div className="mt-2">
                    <strong>Validation Issues:</strong>
                    <ul className="mb-0">
                      {reportData.dataAugmentationValidation.reasons?.map((reason, idx) => {
                        // Check if this is a sync-related issue
                        const isSyncIssue = reason && (
                          reason.includes('No employee data found in PostgreSQL') ||
                          reason.includes('must be synced from MSSQL') ||
                          reason.includes('Data must be synced')
                        );
                        // Check if this is the light load message
                        const isLightLoad = reason && reason.includes('light load');
                        return (
                          <li key={idx}>
                            {isSyncIssue ? (
                              <>
                                <strong style={{ color: '#d32f2f' }}>⚠️ {reason}</strong>
                                <br />
                                <small className="text-muted">
                                  To sync data: Go to the <strong>LTI Data Collection</strong> screen and click the <strong>"Sync Data"</strong> button. 
                                  Data syncs automatically daily at 7 AM, but you can trigger a manual sync at any time.
                                </small>
                              </>
                            ) : isLightLoad ? (
                              <span style={{ color: '#ff9800' }}>ℹ️ {reason}</span>
                            ) : (
                              reason
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </Alert>
            ) : (
              <>

            {/* Show validation info box if data is insufficient */}
            {(() => {
              // Check config from reportData - default to true (show even with insufficient data)
              const showWithInsufficientData = reportData.showDataAugmentationWithInsufficientData !== false;
              const hasInsufficientData = reportData.dataAugmentationValidation && !reportData.dataAugmentationValidation.isValid;
              
              return (
                <>
                  {hasInsufficientData && (
                    <Alert variant="info" className="mb-4">
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}>
                        — denotes insufficient sample in the methodology.
                      </p>
                      <p className="mb-0" style={{ fontSize: '0.875rem' }}>
                        Please contact your Key Accounts Manager (KAM) for assistance.
                      </p>
                    </Alert>
                  )}
                  
                  {/* Show tables if validation passes OR if env var allows showing with insufficient data */}
                  {(!hasInsufficientData || showWithInsufficientData) && reportData.dataAugmentedPayMixPercentiles.length === 0 ? (
                    <div className="text-center my-4">No percentile data available</div>
                  ) : (!hasInsufficientData || showWithInsufficientData) ? (
              (() => {
                const fields = [
                  { key: 'tgp', label: 'TGP' },
                  { key: 'ltiAmount', label: 'LTI Amount' },
                  { key: 'ontargetLtiAmount', label: 'On Target LTI Amount' },
                  { key: 'sti', label: 'STI Amount' },
                  { key: 'onTargetSti', label: 'On Target STI Amount' }
                ];

                const formatCurrency = (value) => {
                  if (value === null || value === undefined) return '—';
                  if (typeof value === 'number') {
                    return value.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 });
                  }
                  return '—';
                };

                return (
                  <div>
                    {hasAllowedRole && (
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={async () => {
                          try {
                            setExportingPercentileData(true);
                            const reportIdToUse = reportId || reportData?.metadata?.reportId || 'unknown';
                            const circleIdToUse = circleId || reportData?.circleId;
                            const circleNameToUse = reportData?.circleName || 'National';
                            const gradingSystemIdToUse = gradingSystemId || reportData?.gradingSystemId;
                            const orgIdToUse = organizationId || orgId || reportData?.metadata?.organizationId;
                            
                            await exportPercentileSourceDataToExcel(
                              reportIdToUse,
                              circleIdToUse,
                              circleNameToUse,
                              gradingSystemIdToUse,
                              orgIdToUse
                            );
                          } catch (error) {
                            console.error('Error exporting percentile source data:', error);
                            alert(`Failed to export percentile source data: ${error.message}`);
                          } finally {
                            setExportingPercentileData(false);
                          }
                        }}
                        disabled={exportingPercentileData || !reportData?.metadata}
                      >
                        {exportingPercentileData ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-file-earmark-excel me-2"></i>
                            Download Source Data
                          </>
                        )}
                      </Button>
                    </div>
                    )}
                    {fields.map((field) => {
                      // Check if any grade has data for this field
                      const hasData = reportData.dataAugmentedPayMixPercentiles.some(
                        (gradeData) => (gradeData[`${field.key}_count`] || 0) > 0
                      );
                      
                      if (!hasData) return null;
                      
                      return (
                        <div key={field.key} className="mb-5">
                          <h6 className="mb-3">{field.label} Percentiles by Survey Grade</h6>
                          <Table striped bordered hover responsive className="mt-3">
                            <thead>
                              <tr>
                                <th>Survey Grade</th>
                                <th>Market Sample</th>
                                <th>Percentage Received</th>
                                <th>10th Percentile</th>
                                <th>25th Percentile</th>
                                <th>50th Percentile (Median)</th>
                                <th>75th Percentile</th>
                                <th>90th Percentile</th>
                                <th>Mean</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.dataAugmentedPayMixPercentiles
                                .filter((gradeData) => (gradeData[`${field.key}_count`] || 0) > 0)
                                .map((gradeData, index) => (
                                  <tr key={index}>
                                    <td><strong>{gradeData.grade || '—'}</strong></td>
                                    <td>
                                      {gradeData[`${field.key}_count`] || 0}
                                      {hasAllowedRole && (gradeData.grade != null && gradeData.grade !== '') && (
                                        <Button
                                          size="sm"
                                          variant="outline-secondary"
                                          className="ms-2"
                                          disabled={exportingGradeCsv !== null}
                                          onClick={async () => {
                                            const grade = gradeData.grade || '';
                                            setExportingGradeCsv(grade);
                                            try {
                                              await downloadPercentileSourceDataCsvForGrade(reportId, circleId, organizationId, grade, field.key);
                                            } catch (err) {
                                              console.error('Error downloading CSV for grade:', err);
                                              alert(`Failed to download CSV: ${err.message || err}`);
                                            } finally {
                                              setExportingGradeCsv(null);
                                            }
                                          }}
                                          title={`Download CSV for grade ${gradeData.grade || '—'}`}
                                        >
                                          {exportingGradeCsv === (gradeData.grade || '') ? (
                                            <Spinner animation="border" size="sm" />
                                          ) : (
                                            <i className="bi bi-download" />
                                          )}
                                        </Button>
                                      )}
                                    </td>
                                    <td>{gradeData[`${field.key}_percentageReceived`] != null && gradeData[`${field.key}_percentageReceived`] !== undefined ? `${Number(gradeData[`${field.key}_percentageReceived`]).toFixed(1)}%` : '—'}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile10`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile25`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile50`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile75`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile90`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_mean`])}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
                  ) : null}
                </>
              );
            })()}

            {/* Pay Mix Stacked Bar Chart - show if validation passes OR if config allows showing with insufficient data */}
            {(() => {
              const showWithInsufficientData = reportData.showDataAugmentationWithInsufficientData !== false;
              const hasInsufficientData = reportData.dataAugmentationValidation && !reportData.dataAugmentationValidation.isValid;
              const shouldShowChart = (!hasInsufficientData || showWithInsufficientData) && reportData.dataAugmentedPayMixPercentiles && reportData.dataAugmentedPayMixPercentiles.length > 0;
              
              return shouldShowChart ? (() => {
              // Prepare chart data - use 50th percentile (median) values for each grade
              const chartData = reportData.dataAugmentedPayMixPercentiles
                .filter((gradeData) => {
                  // Only include grades that have at least one of the displayed fields with data
                  return (gradeData.tgp_count || 0) > 0 || 
                         (gradeData.ontargetLtiAmount_count || 0) > 0 || 
                         (gradeData.onTargetSti_count || 0) > 0;
                })
                .map((gradeData) => {
                  // Use 50th percentile (median) values, defaulting to 0 if not available
                  return {
                    grade: gradeData.grade || '—',
                    tgp: gradeData.tgp_percentile50 || 0,
                    ontargetLtiAmount: gradeData.ontargetLtiAmount_percentile50 || 0,
                    onTargetSti: gradeData.onTargetSti_percentile50 || 0,
                  };
                })
                .sort((a, b) => {
                  // Sort by grade (numeric if possible, otherwise string)
                  const aNum = Number(a.grade);
                  const bNum = Number(b.grade);
                  if (!isNaN(aNum) && !isNaN(bNum)) {
                    return aNum - bNum;
                  }
                  return String(a.grade).localeCompare(String(b.grade));
                });

              if (chartData.length === 0) {
                return null;
              }

              const chartConfig = {
                type: 'bar',
                data: {
                  labels: chartData.map(d => d.grade),
                  datasets: [
                    {
                      label: 'TGP',
                      data: chartData.map(d => d.tgp),
                      backgroundColor: REMCHANNEL_COLORS.steelBlue,
                      borderColor: REMCHANNEL_COLORS.steelBlue,
                      borderWidth: 1,
                    },
                    {
                      label: 'On Target LTI Amount',
                      data: chartData.map(d => d.ontargetLtiAmount),
                      backgroundColor: REMCHANNEL_COLORS.green60,
                      borderColor: REMCHANNEL_COLORS.green60,
                      borderWidth: 1,
                    },
                    {
                      label: 'On Target STI Amount',
                      data: chartData.map(d => d.onTargetSti),
                      backgroundColor: REMCHANNEL_COLORS.rust,
                      borderColor: REMCHANNEL_COLORS.rust,
                      borderWidth: 1,
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'Pay Mix by Survey Grade (50th Percentile / Median)',
                      font: {
                        size: 16,
                        weight: 'bold',
                      },
                      padding: {
                        top: 10,
                        bottom: 20,
                      },
                    },
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        padding: 15,
                        font: {
                          size: 12,
                        },
                        generateLabels: (chart) => {
                          const datasets = chart.data.datasets;
                          return datasets.map((dataset, i) => ({
                            text: formatLegendLabel(dataset.label ?? ""),
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.borderColor,
                            lineWidth: dataset.borderWidth ?? 1,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                          }));
                        },
                      },
                    },
                    tooltip: {
                      enabled: true,
                      callbacks: {
                        label: function(context) {
                          const value = context.parsed.y;
                          return `${formatLegendLabel(context.dataset.label)}: ${value.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 })}`;
                        },
                      },
                    },
                    datalabels: {
                      display: false,
                    },
                  },
                  scales: {
                    x: {
                      stacked: true,
                      title: {
                        display: true,
                        text: 'Grade',
                        font: {
                          size: 12,
                          weight: 'bold',
                        },
                      },
                      ticks: {
                        font: {
                          size: 11,
                        },
                      },
                    },
                    y: {
                      stacked: true,
                      title: {
                        display: true,
                        text: 'Amount (ZAR)',
                        font: {
                          size: 12,
                          weight: 'bold',
                        },
                      },
                      ticks: {
                        callback: function(value) {
                          return value.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
                        },
                        font: {
                          size: 11,
                        },
                      },
                    },
                  },
                },
              };

              return (
                <div className="mt-5">
                  <h6 className="mb-3">Pay Mix Stacked Bar Chart</h6>
                  <div style={{ height: '500px', position: 'relative' }}>
                    <Bar data={chartConfig.data} options={chartConfig.options} />
                  </div>
                </div>
              );
            })() : null;
            })()}
              </>
            )}
          </Accordion.Body>
        </Accordion.Item>
      )}
    </Accordion>
  );
};

export default ReportSections;
